// The Provider types a price per PC in the currency's major unit ("150", "1,500", "149.99"); the api takes integer minor
// units and an ISO 4217 code (spec/06 §1). This is the one place that crosses over: a typed amount is split at the
// decimal point and padded, never multiplied by 100, so 149.99 is exactly 14999 and no float is involved
// (`parseMajorAmount`). The amount is also held to the api's cap before anything is sent.

import type { Money } from '@clavian/api-client';
import { minorUnitDigits } from '@clavian/i18n';

import { parseMajorAmount } from '../../settings/money';

/** The largest single amount the api accepts, in minor units (`MONEY_MAX_MINOR_UNITS`, spec/06 §1.1). */
export const PRICE_MAX_MINOR_UNITS = 10_000_000_000_000;

export type PriceParse =
  | { readonly ok: true; readonly money: Money }
  | { readonly ok: false; readonly reason: 'currency' | 'empty' | 'invalid' | 'fraction' | 'whole' | 'tooLarge' };

/** `INR` or `inr ` → `INR`; anything that is not three letters is not a currency. */
export function normaliseCurrency(input: string): string | null {
  const code = input.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : null;
}

/** A typed price per PC in `currencyInput`'s major unit → exact integer minor units and the code, or why not. Zero is a price (free). */
export function parsePrice(amountInput: string, currencyInput: string): PriceParse {
  const currency = normaliseCurrency(currencyInput);
  if (currency === null) return { ok: false, reason: 'currency' };
  if (amountInput.trim() === '') return { ok: false, reason: 'empty' };
  // No symbol is matched here: the field is already labelled with the currency.
  const parsed = parseMajorAmount(amountInput, { digits: minorUnitDigits(currency), symbol: '' });
  if (!parsed.ok) return { ok: false, reason: parsed.reason };
  if (parsed.minor > PRICE_MAX_MINOR_UNITS) return { ok: false, reason: 'tooLarge' };
  return { ok: true, money: { amount: parsed.minor, currency } };
}
